import {log, log_history} from "@cling/lib.shared.logging"
import {as_SessionUID, create_SessionUID, SessionUID} from "@cling/lib.shared.model"
import type {LogHistory} from "@cling/lib.shared.logging/types"
import {new_ControllablePromise} from "@cling/lib.shared.utils"
import {report_error, report_info} from "@cling/lib.shared.debug"
import {fatal_error_url} from "@cling/lib.web.utils/fatal_error_url"
import {safe_session_storage} from "@cling/lib.web.utils/safe_storage"
import {sensors} from "../debug/sensors"

export class StartWebAppError extends Error {
    reload_page?: boolean
    goto_url?: string
    reload_only_once?: boolean

    constructor(
        message: string,
        {
            reload_page,
            goto_url,
            reload_only_once,
        }: {reload_page?: true; goto_url?: string; reload_only_once?: boolean} = {},
    ) {
        super(message)
        // This is needed according to https://goo.gl/N2zvkR ...
        Object.setPrototypeOf(this, StartWebAppError.prototype)
        this.reload_page = reload_page
        this.goto_url = goto_url
        this.reload_only_once = reload_only_once
    }
}

export function watchdog(
    startup_function: (
        session_uid: SessionUID,
        set_state: (state: "starting" | "waiting for authentication") => void,
    ) => Promise<boolean>,
) {
    try {
        const fail_marker_prefix = "Cling -- start_web_app() failed -- "
        const window_name = window.name || ""
        const start_web_app_failed_before = window_name.startsWith(fail_marker_prefix)
        let session_uid: SessionUID
        if (window_name.startsWith("Cling -- ")) {
            session_uid = as_SessionUID(window_name.substr(window_name.lastIndexOf(" -- ") + 4))
            // Load log history ...
            try {
                const s = safe_session_storage.getItem(`log_history_${session_uid}`)
                if (s) {
                    const a = JSON.parse(s) as LogHistory
                    log_history.unshift(...a)
                }
            } catch {
                // Ignored.
            }
        } else {
            session_uid = create_SessionUID()
        }
        // We store the `session_uid` in the window name, so that
        // we can retrieve it later even if another page was loaded
        // in the meantime ...
        window.name = `Cling -- ${session_uid}`
        const save_log_history = () => {
            try {
                safe_session_storage.setItem(
                    `log_history_${session_uid}`,
                    JSON.stringify(log_history),
                )
            } catch {
                // Ignored.
            }
        }
        window.reload = (reason: string) => {
            log.info(reason + " -- reloading page ...")
            save_log_history()
            location.reload()
        }
        const goto_ = goto
        window.goto = (url: string) => {
            save_log_history()
            goto_(url)
        }
        let state: "starting" | "waiting for authentication" = "starting"
        let waiting_for_authentication_since: undefined | number
        const set_state = (new_state: "starting" | "waiting for authentication") => {
            state = new_state
            if (new_state === "waiting for authentication") {
                waiting_for_authentication_since = Date.now()
            }
        }
        const start_web_app_promise = startup_function(session_uid, set_state)
        const watchdog_promise = new_ControllablePromise<void>()
        const t0 = Date.now()
        let web_app_running = false
        let long_start_reported = false
        const watchdog_id = setInterval(() => {
            if (web_app_running) {
                sensors.validate()
                return
            }
            const now = Date.now()
            if (state === "waiting for authentication") {
                if (now - waiting_for_authentication_since! > 30_000) {
                    watchdog_promise.reject(
                        new StartWebAppError("Could not authenticate within 11 seconds", {
                            goto_url: "/login",
                        }),
                    )
                }
            } else {
                const last_log_history_entry = log_history[log_history.length - 1]
                const t = last_log_history_entry.timestamp2 || last_log_history_entry.timestamp
                if (now - t > 12_222) {
                    report_info("No progress for over 12 seconds")
                }
                if (now - t0 > 30_000 && !long_start_reported) {
                    long_start_reported = true
                    report_info("start_web_app() takes very long")
                }
                if (now - t0 > 66_666) {
                    long_start_reported = true
                    watchdog_promise.reject(new StartWebAppError("start_web_app() took too long"))
                }
            }
        }, 911)
        Promise.race([start_web_app_promise, watchdog_promise])
            .then((web_app_started) => {
                if (web_app_started) {
                    log.debug(`start_web_app() succeeded in ${Math.round(Date.now() - t0)} ms`)
                    web_app_running = true
                } else {
                    // Silent login succeeded ...
                    clearInterval(watchdog_id)
                }
            })
            .catch(async (error) => {
                clearInterval(watchdog_id)
                const m = `start_web_app() failed: ${error.message}`
                if (error instanceof StartWebAppError) {
                    const {reload_page, goto_url, reload_only_once} = error
                    if (reload_page && (!reload_only_once || !start_web_app_failed_before)) {
                        reload(m)
                    } else {
                        goto(goto_url ?? fatal_error_url(error))
                    }
                } else {
                    window.name = fail_marker_prefix + session_uid
                    if (start_web_app_failed_before) {
                        // eslint-disable-next-line no-console
                        console.error(error)
                        goto(fatal_error_url(error))
                    } else {
                        report_error(m, error)
                        reload(m)
                    }
                }
            })
    } catch (error) {
        goto(fatal_error_url(error))
    }
}
